import { useState } from "react";
import { BlockTitle } from "@blocks/Blocks";
import Ad from "@components/Advertisement/Ad";
import AdSettings from "@components/Advertisement/AdSettings";
import Container from "@components/Container/Container";
import GAData from "@components/GAData/GAData";
import IconFactory from "@components/Icon/IconFactory";
import { IconTypeEnum } from "@components/Icon/utils/types";
import MetaTags from "@components/MetaTags/MetaTags";
import EventsAwardsFooter from "@events-awards/components/EventsAwardsFooter";
import EventsAwardsHeader from "@events-awards/EventAwardsHeader";
import EventsAwardsNavigationBar from "@events-awards/EventsAwardsNavigationBarComponents/EventAwardsNavigationBar";
import { AWARDS_EVENTS_PAGE_ROW_DATA } from "@events-awards/utils/constants";
import {
  AwardDataRow,
  EventsAwardsWinnersContextData,
} from "@events-awards/utils/types";

import WinnersParagraphComponent from "./WinnersParagraphComponent";
import WinnersTab from "./WinnersTabs";

/**
 * Component to render event awards winners page content
 */
const EventsAwardsWinnerPageContent = ({
  articles,
  awardType,
}: EventsAwardsWinnersContextData): React.ReactElement => {
  const [showTabs, setShowTabs] = useState(false);

  const years = articles.map(({ title }) => title);
  const [selectedYear, setSelectedYear] = useState(years[0]);

  const awardData: AwardDataRow | undefined = AWARDS_EVENTS_PAGE_ROW_DATA.find(
    (data) => data.awardType === awardType
  );
  const awardPath = awardData?.blockTitleProps?.link || "";

  return (
    <div
      className="min-h-screen w-full"
      data-testid="events-awards-winners-page-component"
    >
      <AdSettings
        adNames={["lb1", "prestitial", "catfish", "abm"]}
        path={`${awardPath}/winners`}
        adTargets={[{ key: "page", value: "listing" }]}
      />

      <GAData
        title={`${awardData?.blockTitleProps?.text} Winners`}
        level2="events awards"
      />

      <MetaTags
        title="THE BUSINESS TIMES - Get the Latest Business & Financial News"
        description={`The Business Times ${awardData?.blockTitleProps?.text} - ${awardData?.description}`}
        ogType="website"
        slug={awardPath}
      />

      <EventsAwardsHeader />

      <Container>
        <EventsAwardsNavigationBar awardType={awardType} />

        <div className="-mx-0 mb-8 w-full bg-gray-150 px-6 py-4 font-public-sans">
          <button
            onClick={() => {
              setShowTabs(!showTabs);
            }}
            className="mb-2 flex items-center gap-2 text-left text-2xl font-bold focus:outline-none"
          >
            <BlockTitle
              text={selectedYear}
              rootClassName="text-4xl md:text-6xl font-medium lg:text-8xl hover:underline"
            />

            <IconFactory
              type={IconTypeEnum.arrowDown}
              x={30}
              y={23}
              size={50}
            />
          </button>

          {showTabs ? (
            <WinnersTab
              selectedYear={selectedYear}
              setSelectedYear={setSelectedYear}
              setShowTabs={setShowTabs}
              years={years}
            />
          ) : null}

          <WinnersParagraphComponent
            selectedYear={selectedYear}
            articles={articles}
          />
        </div>
      </Container>

      <EventsAwardsFooter awardType={awardType} />

      <Ad adType="catfish" />

      <Ad adType="abm" />

      <Ad adType="prestitial" />
    </div>
  );
};

export default EventsAwardsWinnerPageContent;
